import { singleton } from 'tsyringe';
import { IRecommendationDatastoreService } from '../../../shared/interfaces/recommendation-datastore-service.interface';

import { GlobalConfigurations } from '../../../shared/models/global-configurations.model';
import { NonFunctionPropertyNames } from '../../../shared/types';
import {
  KnownRawGlobalConfigs,
  RawGlobalConfigurations,
} from './models/raw-config.model';
import { RawGame } from './models/raw-game.model';
import { RemoteConfigurationsService } from './remote-configurations.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { Game } from '../../models/game.model';
import { Promotion } from '../../models/promotion.model';
import { PromotionType } from './models/raw-promotion.model';

// -----------------------------------------------------------------------------
@singleton()
export class RecommendationDatastoreService
  implements IRecommendationDatastoreService
{
  // ---------------------------------------------------------------------------
  private readonly logger = LoggerService.getCategory(
    'RecommendationDatastoreService',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly remoteConfigsService: RemoteConfigurationsService,
  ) {}

  // ---------------------------------------------------------------------------
  public async getGame(gameId: number): Promise<Game> {
    const rawGame = await this.remoteConfigsService.getRawGame(gameId);
    if (!rawGame) {
      return null;
    }

    const game: Game = {
      id: gameId,
      name: rawGame.name,
      recommendPercent: rawGame.recommendPercent,
      shouldShowOnStart: rawGame.shouldShowOnStart,
      shouldShowOnEnd: rawGame.shouldShowOnEnd,
      gameEventTriggers: rawGame.gameEventsTriggers,
      disableTriggers: rawGame.disableTriggers,
      targetGameId: rawGame.targetGameId,
      offers: await this.getOffers(rawGame),
      promotions: await this.getRecommendedPromotions(rawGame),
    };

    return game;
  }

  // ---------------------------------------------------------------------------
  public async getAllGames(): Promise<Game[]> {
    const rawGames = await this.remoteConfigsService.getRawGames();
    if (!rawGames) {
      return null;
    }

    const games: Game[] = [];

    for (const [key, value] of Object.entries(rawGames)) {
      const game: Game = {
        id: parseInt(key),
        name: value.name,
        recommendPercent: value.recommendPercent,
        shouldShowOnStart: value.shouldShowOnStart,
        shouldShowOnEnd: value.shouldShowOnEnd,
        gameEventTriggers: value.gameEventsTriggers,
        disableTriggers: value.disableTriggers,
        targetGameId: value.targetGameId,
        offers: await this.getOffers(value),
        promotions: await this.getRecommendedPromotions(value),
      };

      games.push(game);
    }

    return games;
  }

  // ---------------------------------------------------------------------------
  public async getGlobalConfigurations(): Promise<GlobalConfigurations> {
    const rawConfigs = await this.remoteConfigsService.getRawGlobalConfigs();
    if (!rawConfigs) {
      this.logger.error('Failed to get server configurations');
      return null;
    }

    const configsMapping = this.generateGlobalConfigsMapping();

    const result = {};

    for (const [key, rawConfig] of configsMapping.entries()) {
      if (!rawConfigs[rawConfig]) {
        continue;
      }

      result[key] = this.getConfigAsNumber(rawConfigs, rawConfig);
    }

    return result as GlobalConfigurations;
  }

  // ---------------------------------------------------------------------------
  public async setEnvironment(environment: string) {
    return this.remoteConfigsService.setNewEnvironment(environment);
  }

  // ---------------------------------------------------------------------------
  private async getRecommendedPromotions(
    rawGame: RawGame,
  ): Promise<Promotion[]> {
    const promotions: Promotion[] = [];

    if (!rawGame.promotions) {
      return promotions;
    }

    for (const rawPromotion of rawGame.promotions) {
      const promotion = await this.toPromotion(rawPromotion);
      if (promotion) {
        promotions.push(promotion);
      }
    }

    return promotions;
  }

  // ---------------------------------------------------------------------------
  private async getOffers(
    rawGame: RawGame,
  ): Promise<Promotion[]> {
    const offers: Promotion[] = [];

    if (!rawGame.offers) {
      return offers;
    }

    for (const rawOffer of rawGame.offers) {
      const offer = await this.toPromotion(rawOffer);
      if (offer) {
        offers.push(offer);
      }
    }

    return offers;
  }

  // ---------------------------------------------------------------------------
  private async toPromotion(rawPromotion: string): Promise<Promotion> {
    const promotionLookup = await this.remoteConfigsService.getPromotionById(
      rawPromotion,
    );
    return {
      displayLimit: promotionLookup?.display_limit,
      id: promotionLookup.id,
      name: promotionLookup.name,
      recommendWeight: promotionLookup.recommend_weight,
      geoRestriction: promotionLookup.geo_restriction,
      type: promotionLookup.type,
    };
  }

  // ---------------------------------------------------------------------------
  private generateGlobalConfigsMapping() {
    return new Map<
      NonFunctionPropertyNames<GlobalConfigurations>,
      KnownRawGlobalConfigs
    >([
      [
        'autoDismissPromotionsInSeconds',
        KnownRawGlobalConfigs.AutoDismissPromotionSecs,
      ],
      [
        'minimumVisibleTimeToCountInSeconds',
        KnownRawGlobalConfigs.MinimumVisibleTimeToCountInSeconds,
      ],
      [
        'passiveAnalyticsPersistentPhasing',
        KnownRawGlobalConfigs.PassiveAnalyticsPersistentPhasing,
      ],
      [
        'minimumGameSessionTimeToDisplayPromotionMins',
        KnownRawGlobalConfigs.MinimumGameSessionTimeToDisplayPromotionMins,
      ],
      [
        'minNumberOfAdsDisplayedBeforeShow',
        KnownRawGlobalConfigs.MinNumberOfAdsDisplayedBeforeShow,
      ],
      [
        'cooldownFromPreviousPromoInHours',
        KnownRawGlobalConfigs.CooldownFromPreviousPromoInHours,
      ],
      [
        'cooldownFromPreviousPromoOfferInHours',
        KnownRawGlobalConfigs.CooldownFromPreviousPromoOfferInHours,
      ],
    ]);
  }

  // ---------------------------------------------------------------------------
  private getConfigAsNumber(
    rawConfigs: RawGlobalConfigurations,
    name: KnownRawGlobalConfigs,
  ): number {
    return parseInt(rawConfigs[name], 10);
  }
}
